import type {
  AuthMe,
  AuthProvider,
  CartView,
  VoucherStatus,
  Nachweis,
  CheckoutRequest,
  Collection,
  CollectionPage,
  Fulfilment,
  Order,
  PageLink,
  PaymentMethod,
  PaymentStart,
  PaymentStatus,
  Product,
  ProductList,
  ProductQuery,
  ShopConfig,
  ShopPage,
} from './types'
import * as mock from './mock'
import { ApiError } from './error'

// Der Fehlertyp gehört zur Schnittstelle, darum hier weitergereicht.
export { ApiError } from './error'

/**
 * Die Basis steht relativ — bewusst ohne Rechnernamen.
 *
 * Der Laden erkennt seine Organisation am Host der Anfrage. Ein absoluter
 * Pfad auf eine feste Adresse würde alle Läden auf denselben Mandanten
 * schicken, sobald irgendwo eine Umgebungsvariable stehen bliebe.
 */
const BASE = '/api/shop/v1'

/**
 * Beispieldaten statt Server, per VITE_MOCK=1 eingeschaltet.
 *
 * Damit lassen sich die Seiten ansehen, bevor die Schnittstelle steht. Der
 * Schalter wird beim Bauen ausgewertet, sodass die Beispieldaten aus einem
 * Betriebs-Build herausfallen und nicht versehentlich einen leeren Laden
 * füllen.
 */
const USE_MOCK = import.meta.env.VITE_MOCK === '1' || import.meta.env.VITE_MOCK === 'true'

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response
  try {
    response = await fetch(BASE + path, {
      ...init,
      // Warenkorb und Anmeldung hängen an Cookies, die der Server setzt —
      // HttpOnly, also für Skripte unsichtbar. Ohne diese Zeile schickt fetch
      // sie nicht mit, und jeder Aufruf bekäme einen frischen, leeren
      // Warenkorb: Der Kunde legt etwas hinein und findet es nie wieder.
      credentials: 'include',
      headers: { Accept: 'application/json', ...init?.headers },
    })
  } catch (cause) {
    // Abbrüche gibt der Aufrufer unverändert weiter — sie sind kein Fehler,
    // sondern ein Seitenwechsel.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError(0, 'Der Laden ist gerade nicht erreichbar.')
  }

  if (!response.ok) {
    // Das Backend meldet Fehler als { "error": "..." }. Wenn das fehlschlägt,
    // zählt der Status — eine HTML-Fehlerseite als Meldung anzuzeigen hilft
    // niemandem.
    let message = `Fehler ${response.status}`
    try {
      const body = (await response.json()) as { error?: string }
      if (body?.error) message = body.error
    } catch {
      /* Antwort ohne JSON — der Status bleibt die Aussage. */
    }
    throw new ApiError(response.status, message)
  }

  return (await response.json()) as T
}

function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(path, signal ? { signal } : undefined)
}

function post<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  })
}

function put<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  })
}

function del<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: 'DELETE', ...(signal ? { signal } : {}) })
}

/**
 * Holt die Bestellung aus der Antwort, mit oder ohne Umschlag.
 *
 * Der Vertrag nennt für die Kasse ausdrücklich `{ order: … }`, für die
 * Nachschau nur „Bestellstatus". Beide Formen anzunehmen kostet drei Zeilen
 * und erspart einen Laden, der nach dem Bestellen eine leere Seite zeigt,
 * weil ein Umschlag fehlt oder einer zu viel da ist.
 */
function entpackeBestellung(antwort: unknown): Order {
  const umschlag = antwort as { order?: Order } | Order
  if (umschlag && typeof umschlag === 'object' && 'order' in umschlag && umschlag.order) {
    return umschlag.order
  }
  return antwort as Order
}

function query(params: ProductQuery): string {
  const search = new URLSearchParams()
  if (params.search) search.set('search', params.search)
  if (params.collection) search.set('collection', params.collection)
  if (params.page && params.page > 1) search.set('page', String(params.page))
  if (params.pageSize) search.set('pageSize', String(params.pageSize))
  const q = search.toString()
  return q ? `?${q}` : ''
}

export function getConfig(signal?: AbortSignal): Promise<ShopConfig> {
  return USE_MOCK ? mock.getConfig() : get<ShopConfig>('/config', signal)
}

export function getCollections(signal?: AbortSignal): Promise<Collection[]> {
  return USE_MOCK ? mock.getCollections() : get<Collection[]>('/collection', signal)
}

export function getCollection(handle: string, signal?: AbortSignal): Promise<CollectionPage> {
  return USE_MOCK
    ? mock.getCollection(handle)
    : get<CollectionPage>(`/collection/${encodeURIComponent(handle)}`, signal)
}

export function getProducts(params: ProductQuery, signal?: AbortSignal): Promise<ProductList> {
  return USE_MOCK ? mock.getProducts(params) : get<ProductList>(`/product${query(params)}`, signal)
}

export function getProduct(handle: string, signal?: AbortSignal): Promise<Product> {
  return USE_MOCK
    ? mock.getProduct(handle)
    : get<Product>(`/product/${encodeURIComponent(handle)}`, signal)
}

/**
 * Die Verweise auf die Seiten des Ladens — Impressum, Widerruf, Datenschutz.
 *
 * Nur veröffentlichte, in der Reihenfolge des Ladens. Ein eigener Aufruf und
 * kein Teil der Grunddaten: Die Liste ändert sich unabhängig vom Aussehen, und
 * der Fußbereich darf ruhig einen Wimpernschlag später stehen.
 */
export function getPages(signal?: AbortSignal): Promise<PageLink[]> {
  return USE_MOCK ? mock.getPages() : get<PageLink[]>('/page', signal)
}

/** Eine Seite mit ihrem Text. Der Text ist Markdown, kein HTML. */
export function getPage(handle: string, signal?: AbortSignal): Promise<ShopPage> {
  return USE_MOCK ? mock.getPage(handle) : get<ShopPage>(`/page/${encodeURIComponent(handle)}`, signal)
}

// ---------- Warenkorb ----------
//
// Er liegt auf dem Server, nicht im Browser. Die Kennung steht in einem
// Cookie, das kein Skript lesen kann; was drinliegt, entscheidet allein der
// Server. Das ist nicht nur sauberer, es ist der einzige Weg, auf dem ein
// Warenkorb Gerätewechsel und Anmeldung überlebt — und auf dem ein gekürzter
// Bestand im Korb ankommt, statt erst an der Kasse.

/**
 * Der Warenkorb, wie ihn der Server rechnet.
 *
 * Zustellart und Land gehen mit, weil Versandkosten, Freigrenze und
 * Hinderungsgründe daran hängen. Wer sie wegließe, bekäme die Zahlen für
 * Versand nach Deutschland — auch dann, wenn der Kunde gerade Abholung
 * gewählt hat.
 */
export function getCart(
  fulfilment: Fulfilment,
  country: string,
  signal?: AbortSignal,
): Promise<CartView> {
  if (USE_MOCK) return mock.getCart(fulfilment, country)
  return get<CartView>(
    `/cart?fulfilment=${encodeURIComponent(fulfilment)}&country=${encodeURIComponent(country)}`,
    signal,
  )
}

/**
 * Setzt die Menge einer Zeile — ein Weg für Hinzufügen, Ändern und Entfernen.
 *
 * Es ist dieselbe Frage: wie viel davon soll drin sein. Menge 0 entfernt die
 * Zeile. Zurück kommt der ganze Warenkorb und nicht nur die Zeile, weil sich
 * Versand, Freigrenze und Hinderungsgründe mitändern.
 */
export function setCartLine(
  variantId: string,
  quantity: number,
  fulfilment: Fulfilment,
  country: string,
): Promise<CartView> {
  if (USE_MOCK) return mock.setCartLine(variantId, quantity, fulfilment, country)
  return put<CartView>(
    `/cart/line?fulfilment=${encodeURIComponent(fulfilment)}&country=${encodeURIComponent(country)}`,
    { variantId, quantity },
  )
}

/**
 * Einen Rabattcode eintragen — oder mit leerem Text wieder entfernen.
 *
 * Zurück kommt der ganze Warenkorb und nicht ein „gültig/ungültig": Ob der
 * Code greift und was er bewirkt, steht ohnehin darin, samt dem Satz, warum
 * nicht. Eine eigene Antwort wäre eine zweite Wahrheit daneben.
 */
export function setCartDiscount(
  code: string,
  fulfilment: Fulfilment,
  country: string,
): Promise<CartView> {
  if (USE_MOCK) return mock.setCartDiscount(code, fulfilment, country)
  return put<CartView>(
    `/cart/discount?fulfilment=${encodeURIComponent(fulfilment)}&country=${encodeURIComponent(country)}`,
    { code },
  )
}

/**
 * Nachsehen, was auf einem Wertgutschein liegt.
 *
 * Nur nachsehen, nicht einlösen: Gezogen wird erst beim Bestellen, und zwar in
 * derselben Transaktion wie die Bestellung — sonst bucht ein abgebrochener
 * Kauf Guthaben ab.
 */
export function checkVoucher(code: string, signal?: AbortSignal): Promise<VoucherStatus> {
  if (USE_MOCK) return mock.checkVoucher(code)
  return get<VoucherStatus>(`/voucher/${encodeURIComponent(code)}`, signal)
}

export function clearCart(fulfilment: Fulfilment, country: string): Promise<CartView> {
  if (USE_MOCK) return mock.clearCart(fulfilment, country)
  return del<CartView>(
    `/cart?fulfilment=${encodeURIComponent(fulfilment)}&country=${encodeURIComponent(country)}`,
  )
}

// ---------- Anmeldung ----------

/** Die Anmeldemöglichkeiten dieses Ladens. Leere Liste heißt: keine anbieten. */
export function getAuthProviders(signal?: AbortSignal): Promise<AuthProvider[]> {
  if (USE_MOCK) return mock.getAuthProviders()
  return get<AuthProvider[]>('/auth/provider', signal)
}

export function getMe(signal?: AbortSignal): Promise<AuthMe> {
  if (USE_MOCK) return mock.getMe()
  return get<AuthMe>('/auth/me', signal)
}

export function logout(): Promise<unknown> {
  if (USE_MOCK) return mock.logout()
  return post<unknown>('/auth/logout', {})
}

/** Die Bestellungen des Angemeldeten — ohne Nummer und E-Mail eingeben zu müssen. */
export function getMyOrders(signal?: AbortSignal): Promise<Order[]> {
  if (USE_MOCK) return mock.getMyOrders()
  return get<Order[]>('/auth/order', signal)
}

/**
 * Meldet an — durch Verlassen der Seite, nicht durch einen Aufruf.
 *
 * Kein fetch: Der Server antwortet mit einer Weiterleitung zum Anbieter, und
 * eine Weiterleitung, die ein fetch verfolgt, landet im Skript statt im
 * Browser. Der Kunde müsste beim Anbieter etwas eingeben, das er nie zu sehen
 * bekäme.
 */
export function startLogin(key: string, returnTo: string): void {
  if (USE_MOCK) {
    void mock.startLogin(key, returnTo)
    return
  }
  window.location.assign(
    `${BASE}/auth/${encodeURIComponent(key)}/start?returnTo=${encodeURIComponent(returnTo)}`,
  )
}

// ---------- Bezahlen ----------

/**
 * Was an dieser Kasse zur Wahl steht.
 *
 * Eigener Aufruf und nicht nur in den Grunddaten, weil die Antwort davon
 * abhängt, ob versandt oder abgeholt wird: Barzahlung bei Abholung an eine
 * Lieferadresse anzubieten wäre keine Zahlungsart, sondern ein Missverständnis.
 */
export function getPaymentMethods(
  fulfilment: Fulfilment,
  signal?: AbortSignal,
): Promise<PaymentMethod[]> {
  if (USE_MOCK) return mock.getPaymentMethods(fulfilment)
  return get<PaymentMethod[]>(
    `/payment-method?fulfilment=${encodeURIComponent(fulfilment)}`,
    signal,
  )
}

/**
 * Bestellen.
 *
 * Ohne Positionen: Was bestellt wird, steht im Warenkorb auf dem Server. Preise
 * und Versandkosten rechnet er ebenfalls — er ist der Einzige, dessen Zahlen
 * später auf der Rechnung stehen.
 */
export async function postCheckout(
  daten: CheckoutRequest,
  signal?: AbortSignal,
): Promise<{ bestellung: Order; token: string }> {
  if (USE_MOCK) return mock.postCheckout(daten)
  const roh = await post<unknown>('/checkout', daten, signal)
  return {
    bestellung: entpackeBestellung(roh),
    // Der Schlüssel kommt genau einmal: hier. Er gehört in die Adresse, sonst
    // ist die Bestellung nach einem Neuladen nicht mehr auffindbar.
    token: String((roh as { token?: unknown })?.token ?? ''),
  }
}

/**
 * Eine Bestellung nachschlagen.
 *
 * Zwei Nachweise, und beide genügen für sich:
 *
 *   token  der Schlüssel aus der Adresse. So kommt jemand zurück, der neu
 *          geladen hat oder von einer Bezahlseite kommt — ohne zu tippen.
 *   email  die E-Mail zur Nummer, für jemanden, der nur noch die
 *          Bestätigungsmail hat.
 *
 * Die Nummer allein reicht nie: Sie ist kurz genug zum Durchprobieren.
 */
export async function getOrder(
  nummer: string,
  nachweis: Nachweis,
  signal?: AbortSignal,
): Promise<Order> {
  if (USE_MOCK) return mock.getOrder(nummer, nachweis)
  return entpackeBestellung(
    await get<unknown>(`/order/${encodeURIComponent(nummer)}?${frage(nachweis)}`, signal),
  )
}

/**
 * Die Adresse der Rechnung — ein Verweis, kein Aufruf.
 *
 * Absichtlich kein fetch: Ein PDF, das ein Skript herunterlädt, muss dann als
 * Blob in den Speicher, bekommt einen erfundenen Dateinamen und lässt sich auf
 * dem Telefon nicht im Betrachter öffnen. Ein <a href> überlässt das dem
 * Browser, der es besser kann — samt Fortschritt, Abbruch und „Öffnen mit".
 *
 * Nummer und E-Mail sind auch hier der Schlüssel, wie bei der Nachschau.
 */
export function invoiceUrl(nummer: string, email: string): string {
  return `${BASE}/order/${encodeURIComponent(nummer)}/invoice.pdf?email=${encodeURIComponent(email)}`
}

/**
 * Fordert den Betrag über die gewählte Art an.
 *
 * Nummer und E-Mail sind auch hier der Schlüssel, wie bei der Nachschau. Was
 * zurückkommt, hängt an `kind`: ein Code, ein Ziel oder ein Satz.
 */
export function startPayment(
  nummer: string,
  nachweis: Nachweis,
  method: string,
  signal?: AbortSignal,
): Promise<PaymentStart> {
  if (USE_MOCK) return mock.startPayment(nummer, nachweis, method)
  return post<PaymentStart>(
    `/order/${encodeURIComponent(nummer)}/pay`,
    { ...nachweis, method },
    signal,
  )
}

/**
 * Der Nachweis als Abfrageteil einer Adresse.
 *
 * An einer Stelle, damit nicht die eine Funktion den Schlüssel mitschickt und
 * die nächste nur die E-Mail — genau so entstand der Weg, auf dem der
 * Zahlungsbereich fehlte.
 */
function frage(nachweis: Nachweis): string {
  return nachweis.token
    ? `token=${encodeURIComponent(nachweis.token)}`
    : `email=${encodeURIComponent(nachweis.email ?? '')}`
}

/**
 * Fragt, ob bezahlt wurde.
 *
 * Gefragt wird beim Server, der wiederum den Anbieter fragt. Das Ladenfenster
 * kann nie behaupten, dass bezahlt wurde — es kann nur fragen.
 */
export function getPaymentStatus(
  nummer: string,
  nachweis: Nachweis,
  signal?: AbortSignal,
): Promise<PaymentStatus> {
  if (USE_MOCK) return mock.getPaymentStatus(nummer, nachweis)
  return get<PaymentStatus>(
    `/order/${encodeURIComponent(nummer)}/pay?${frage(nachweis)}`,
    signal,
  )
}

/** Bricht die laufende Anforderung ab, damit sie niemand später noch bezahlt. */
export function cancelPayment(nummer: string, nachweis: Nachweis): Promise<unknown> {
  if (USE_MOCK) return mock.cancelPayment(nummer, nachweis)
  return post<unknown>(`/order/${encodeURIComponent(nummer)}/pay/cancel`, { ...nachweis })
}
